import { useEffect } from 'react';
import { useAppStore } from '../state/store';
import { useInstallPrompt } from '../state/useInstallPrompt';
import { IconClear, IconDownload } from './Icons';

export function InfoSheet() {
  const open = useAppStore((s) => s.infoOpen);
  const setOpen = useAppStore((s) => s.setInfoOpen);
  const { canInstall, installed, iosHint, install } = useInstallPrompt();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={() => setOpen(false)} role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Come funziona ShadowRoute"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="icon-btn modal__close"
          aria-label="Chiudi"
          onClick={() => setOpen(false)}
        >
          <IconClear size={18} />
        </button>
        <div className="modal__brand">
          <img src={`${import.meta.env.BASE_URL}favicon.svg`} alt="" width={44} height={44} />
          <div>
            <h2>ShadowRoute</h2>
            <p className="t-sub">Percorsi a piedi con più ombra, calcolati nel tuo browser.</p>
          </div>
        </div>

        <h3>Come funziona</h3>
        <ol>
          <li>
            Scarico da <strong>OpenStreetMap</strong> vie pedonali, edifici (con altezza reale o
            stimata dai piani), alberi, filari e aree verdi attorno al tragitto.
          </li>
          <li>
            Calcolo la <strong>posizione del sole</strong> (algoritmo NOAA) per l’orario di partenza
            e per ogni quarto d’ora successivo: l’ombra di ogni tratto è valutata dove sarà il sole{' '}
            <em>quando passerai di lì</em>.
          </li>
          <li>
            Ogni tratto è campionato ogni ~9 m: un punto è in ombra se un edificio abbastanza alto
            (o la chioma di un albero) sta tra lui e il sole. Gallerie e portici sono ombra piena,
            boschi e parchi ombra parziale.
          </li>
          <li>
            Il routing (A*) minimizza <code>lunghezza × (1 + α · sole)</code>: con α = 0 ottieni il
            più breve, con α alto compri ombra pagando distanza. <strong>Consigliato</strong> prova
            più valori e sceglie il miglior rapporto ombra/deviazione.
          </li>
        </ol>

        <h3>Cosa c’è nel pannello</h3>
        <div className="modal__features">
          <div className="feature">
            <strong>Meteo e luce</strong>
            Alba, tramonto, giorno e notte polare, nuvole, UV e caldo (Open-Meteo). Il meteo
            consiglia, non decide: il calcolo non si blocca mai.
          </div>
          <div className="feature">
            <strong>Quando partire?</strong>
            L’ombra dello stesso percorso partendo ogni mezz’ora nelle prossime 6 ore.
          </div>
          <div className="feature">
            <strong>Avvia</strong>
            Navigazione con la tua posizione: quanto manca, ombra davanti a te, avviso se esci dal
            percorso.
          </div>
          <div className="feature">
            <strong>Google Maps</strong>
            Un tragitto unico con il minimo di punti di passaggio necessario per seguire le strade
            all’ombra. GPX per app e smartwatch.
          </div>
        </div>

        <h3>Installa l’app</h3>
        <div className="install">
          {installed ? (
            <p className="t-sub">ShadowRoute è installata: si apre a tutto schermo come un’app.</p>
          ) : canInstall ? (
            <>
              <p className="t-sub">
                Aggiungila alla schermata Home: si apre a tutto schermo, resta pronta anche offline
                e la posizione funziona come nelle app native.
              </p>
              <button
                type="button"
                className="btn btn--sm btn--primary"
                onClick={() => void install()}
              >
                <IconDownload size={16} /> Installa ShadowRoute
              </button>
            </>
          ) : iosHint ? (
            <p className="t-sub">
              Su iPhone e iPad: tocca <strong>Condividi</strong> in Safari e poi{' '}
              <strong>«Aggiungi alla schermata Home»</strong>.
            </p>
          ) : (
            <p className="t-sub">
              Dal menu del browser puoi aggiungere ShadowRoute alla schermata Home o installarla
              come app.
            </p>
          )}
        </div>

        <h3>Limiti onesti</h3>
        <ul>
          <li>
            Se OSM non ha l’altezza di un edificio uso stime prudenti; gli alberi ci sono solo dove
            la community li ha mappati.
          </li>
          <li>Niente ombre di colline o terreno; le nuvole sono un consiglio, non un calcolo.</li>
          <li>
            Google Maps ricalcola il tragitto tra i punti di passaggio: può differire di poco.
          </li>
        </ul>
        <p className="modal__credits">
          Dati © OpenStreetMap contributors (ODbL) · basemap CARTO · meteo Open-Meteo · ricerca
          Nominatim. Routing e ombre calcolati interamente nel tuo browser.
        </p>
      </div>
    </div>
  );
}
